import { $http } from '@/common/api/http.service';
import { $httpFhirApi } from '@/common/api/httpFhir.service';
import timezoneApi from '@/common/api/timezone.api';
import { firstLine, getUrlFromCanonical, htmlToText } from '@/common/core';
import { crud } from '@/common/crud';
import { canonicalizeItemIdentity, isRetiredWidget, regenerateItemLinkIds } from '@/common/formBuilder';
import { resolvePerDiemApplicableCodes } from '@/common/perDiemSuppression';
import { signerNameFromResource } from '@/common/signerName';
import { useAuthFlow } from '@/common/workflowEngine/useAuthFlow';
import { useChargeItemFlow } from '@/common/workflowEngine/useChargeItemFlow';
import { useListFlow } from '@/common/workflowEngine/useListFlow';
import { MANAGED_FORM_SUPPRESSION_QUERY, MANAGED_FORM_SUPPRESSION_SYSTEM, MANAGED_FORM_SUPPRESSION_VALUE } from '@/common/workflowEngine/useProfileFlow';
import { useVitalsFlow } from '@/common/workflowEngine/useVitalsFlow';
import { useWorkflow } from '@/common/workflowEngine/useWorkflow';
import { useQuestionnaireApi } from '@/composables/api/useQuestionnaireApi';
import { Encounter, Observation, Procedure, Questionnaire, QuestionnaireAnswerOption, QuestionnaireResponseItemAnswer, Reference, Task } from '@/fhirworks';
import ProcedureModifier from '@/fhirworks/ProcedureModifier';
import { useAuthStore } from '@/stores/auth';
import { useNotificationStore } from '@/stores/notification';
import axios from 'axios';
import { addDays, format, sub } from 'date-fns';
import uniqBy from 'lodash/uniqBy';
import { v4 as uuidv4, v5 as uuidv5 } from 'uuid';

const { postTransaction, getReference } = useWorkflow();

// Cross-form propagation (updateCatalog* / syncFieldSetAcrossForms) fans an edit out to every
// active/draft form sharing the field, and without an explicit _count the server returns one page —
// silently skipping the overflow forms, so the edit lands as a partial write. Matches BATCH_COUNT in
// composables/useEBTxLibrary. Still a ceiling, not paging: nothing here follows Bundle link[next].
const PROPAGATION_FETCH_COUNT = 5000;

// Helper functions
const getTasksForQuestionnaireResponse = async function (id) {
    let task = null;
    let subTasks = null;
    await $httpFhirApi
        .get('Task?focus=' + id + '&_include=based-on:Task&_revinclude=Task:part-of')
        .then((result) => {
            const data = result.data || [];
            // Primary lookup: main form Task that is explicitly linked to this QR.
            task = data.find((item) => item.focus?.id === id && item.code.codeZero === 'form');
            if (!task) {
                // Fallback: main form Task can be included via basedOn/partOf and may not carry QR focus.
                // Exclude parent group tasks (no focus) to avoid creating orphan signature tasks.
                task = data.find((item) => item.code.codeZero === 'form' && item.focus);
            }
            subTasks = task ? data.filter((item) => item.id !== task.id) : data;
        })
        .catch((err) => {
            console.error('[getTasksForQuestionnaireResponse] Task bundle load failed; assignments unavailable for QR', id, err);
            // Throw after logging to prevent partial success (QR updated, Task state missing).
            throw new Error(`[getTasksForQuestionnaireResponse] Failed to load Task bundle for QuestionnaireResponse ${id}: ${err?.message || String(err)}`, {
                cause: err,
            });
        });

    if (task && subTasks?.some((item) => item.groupTask === task.id)) {
        task.temp.groupTask = task.id;
    }

    return [task, subTasks];
};

// Helper function to check if claims reference a specific form
const hasClaimsForForm = (claims, questionnaireResponseId) => {
    return claims.some((claim) => {
        return (
            claim.resourceType === 'Claim' &&
            claim.status === 'active' &&
            claim.supportingInfo?.some((info) => info.code?.text && info.code.text.includes(`QuestionnaireResponse/${questionnaireResponseId}`))
        );
    });
};

const populateSignerReviewerTask = function (task, owner, action) {
    let assignment = new Task();
    assignment.for = task.for;
    assignment.basedOn = task.basedOn;
    assignment.groupTask = task.groupTask || task.temp.groupTask;
    assignment.encounter = task.encounter;
    assignment.intent = 'unknown';
    assignment.status = 'accepted';
    assignment.authoredOn = new Date().toISOString();
    assignment.kanban = { priority: Date.now().toString() };
    assignment.code = { text: 're: ' + task.code.text, coding: [{ code: 'assignment', display: action.charAt(0).toUpperCase() + action.slice(1), version: action }] };
    assignment.restriction = { period: { start: new Date().toISOString(), end: addDays(new Date(), 7).toISOString() } };
    assignment.partOf = [{ id: task.id, resourceType: 'Task' }];

    // if it's a group note.  We want the signature/reviewer task to be connected to the main task, not the patients individual task
    if (assignment.groupTask) {
        assignment.partOf = [{ id: task.basedOn.find((item) => item.resourceType === 'Task')?.id, resourceType: 'Task' }];
    }

    assignment.owner = owner;
    assignment.focus = task.focus || undefined;
    return assignment;
};

const getSubtasks = async function (taskId) {
    let subTasks = null;
    await $httpFhirApi.get('Task?part-of=' + taskId).then((result) => {
        subTasks = result.data;
    });

    return subTasks;
};

// unlock managed items when making a standalone copy
const unlockFormItems = function (item) {
    item.forEach((i) => {
        i.locked = undefined;
        if (i.item.length) unlockFormItems(i.item);
    });
};

const findQuestionnaireItem = function (questionnaire, predicate) {
    for (const item of questionnaire.item || []) {
        if (predicate(item)) return item;
        if (item.item?.length) {
            const found = findQuestionnaireItem(item, predicate);
            if (found) return found;
        }
    }
    return null;
};

const filterQuestionnaireItems = function (questionnaire, predicate, results = []) {
    for (const item of questionnaire.item || []) {
        if (predicate(item)) results.push(item);
        if (item.item?.length) {
            filterQuestionnaireItems(item, predicate, results);
        }
    }
    return results;
};

const findQuestionnaireItemByLinkId = (questionnaire, linkId) => findQuestionnaireItem(questionnaire, (item) => item.linkId === linkId);

// Flatten a questionnaire into an ordered nav list: each included top-level item at
// indent 0, followed by its included direct children at indent 1. Children only appear
// under an included parent.
// Retired widgets are dropped ahead of the caller's include predicates, so every nav surface
// (builder, filler, portal) is guaranteed not to link to a section that no longer renders.
export const collectNavigationItems = (questionnaire, { includeTopLevel, includeNested }) => {
    const result = [];
    for (const item of questionnaire?.item || []) {
        if (isRetiredWidget(item) || !includeTopLevel(item)) continue;
        result.push({ item, indent: 0 });
        for (const child of item.item || []) {
            if (!isRetiredWidget(child) && includeNested(child)) {
                result.push({ item: child, indent: 1 });
            }
        }
    }
    return result;
};

// Shape a collectNavigationItems entry into the { value, text, icon, indent } the nav panels
// render. `icons` maps clinical-widget linkIds to { icon, text }. The label transform is owned here,
// not injected, so every nav surface is guaranteed the same one.
// `preferWidgetText` labels a custom widget with the canonical clinical-widget text; pass false
// (portal) to keep the per-form item text, which matches the on-page heading.
export const toNavEntry = ({ item, indent }, { icons = [], preferWidgetText = true }) => {
    const widget = icons.find((i) => i.id === item.linkId);
    let icon = widget?.icon;
    if (item.displayCategory?.codeZero === 'signatureBlock') {
        icon = ['far', 'signature'];
    } else if (item.type === 'group') {
        icon = ['far', 'rectangle-list'];
    } else if (!icon) {
        icon = ['far', 'list-ul'];
    } else if (preferWidgetText && item.customWidget) {
        return { value: item.linkId, text: widget?.text, icon, indent };
    } else if (item.widgetProps?.problemList) {
        return { value: item.linkId, text: 'Problem list', icon: 'fa-kit fa-regular-thought-bubble-circle-exclamation', indent };
    }
    // Cut on the stored HTML (`bnText`) before converting: `item.text` has already lost the break — a
    // heading followed by a heading plaintexts with no separator — so the cut is unrecoverable.
    return { value: item.linkId, text: htmlToText(firstLine(item.bnText || item.text)), icon, indent };
};

const filterQuestionnaireItemByLinkId = (questionnaire, linkId) => filterQuestionnaireItems(questionnaire, (item) => item.linkId === linkId);

const loopItems = function (items, questionnaire, callback) {
    items.forEach((i) => {
        let qItem = findQuestionnaireItemByLinkId(questionnaire, i.linkId);
        if (!qItem) return;
        if (qItem?.type === 'question') {
            callback(i, true);
        } else if (i.item.length) {
            loopItems(i.item, questionnaire, callback);
        } else {
            callback(i);
        }
    });
};

// _elements returns an item's children only to the depth it names, and a field set inside a
// section keeps its fields three levels down. Four covers a field set in a nested section.
const EXTRACTION_ITEM_DEPTH = 4;
const EXTRACTION_ELEMENTS = Array.from({ length: EXTRACTION_ITEM_DEPTH }, (_, index) => 'Questionnaire' + '.item'.repeat(index + 1))
    .flatMap((path) => ['linkId', 'type', 'code'].map((field) => `${path}.${field}`))
    .join(',');

// The most field set rows latest value carries forward from one form.
const FIELD_SET_ROW_FETCH_COUNT = 50;

// A field set row's position in its form, stamped on the row's panel Observation. Rows locked
// together share an effective time, so this is the only thing that orders them.
const FIELD_SET_ROW_SYSTEM = 'http://fhir.bestnotes.com/field-set-row';
const fieldSetRowIndex = (observation) => {
    const value = observation.identifier?.find((identifier) => identifier.system === FIELD_SET_ROW_SYSTEM)?.value;
    return value === undefined ? Infinity : Number(value);
};
// The panel written most recently, by its effective instant; null when none carries one.
const newestPanel = (entries) =>
    entries
        .filter((entry) => entry.hasMember?.length && entry.effective?.instant)
        .reduce((newest, entry) => (!newest || Date.parse(entry.effective.instant) > Date.parse(newest.effective.instant) ? entry : newest), null);

const byFieldSetRow = (a, b) => {
    const left = fieldSetRowIndex(a);
    const right = fieldSetRowIndex(b);
    return left === right ? 0 : left < right ? -1 : 1;
};

// The forms an answer can take in an Observation: its value as written, or the rich text the
// lock stored as a string (see addObservations).
const answerKeys = (answer) => {
    const keys = [JSON.stringify(answer?.value)];
    if (answer?.renderingXhtml) keys.push(JSON.stringify({ string: answer.renderingXhtml }));
    return keys;
};

/**
 * Where each row sits in the form it came from, found by matching values.
 *
 * Rows locked before rows were numbered carry no position, but the form they came from still
 * holds them in the order they were entered: each row is a response item with child answers.
 * A panel belongs to the row whose answers hold the most of its members' values. Returns the
 * row's position among the items sharing its linkId, or Infinity when nothing matches.
 */
const sourceRowPosition = (panel, membersById, sourceResponse) => {
    const memberKeys = (panel.hasMember || []).map((reference) => JSON.stringify(membersById.get(reference.id)?.value)).filter((key) => key !== undefined);
    const positionByLinkId = new Map();
    let best = { score: 0, position: Infinity };

    for (const row of sourceResponse?.item || []) {
        if (!row.item?.length) continue;
        const position = positionByLinkId.get(row.linkId) ?? 0;
        positionByLinkId.set(row.linkId, position + 1);

        const rowKeys = new Set(row.item.flatMap((child) => (child.answer || []).flatMap(answerKeys)));
        const score = memberKeys.filter((key) => rowKeys.has(key)).length;
        if (score > best.score) best = { score, position };
    }

    return best.position;
};

/**
 * Order the rows of forms locked before rows were numbered, by where they sit in their form.
 * One read covers every field set that needs it. A failed read leaves the rows in the order the
 * server returned them rather than failing the form.
 */
const orderUnnumberedRows = async (unnumbered, returnResults) => {
    if (!unnumbered.length) return;

    const responseIds = [...new Set(unnumbered.map((entry) => entry.responseId))];
    let responses;
    try {
        responses = (await $httpFhirApi.get('QuestionnaireResponse?_id=' + responseIds.join(',') + '&_elements=item')).data || [];
    } catch (error) {
        console.error('Latest value: could not read the forms to order field set rows.', { responseIds, error });
        return;
    }
    const responseById = new Map(responses.map((response) => [response.id, typeof response.toJSON === 'function' ? response.toJSON() : response]));

    for (const { linkId, panels, members, responseId } of unnumbered) {
        const sourceResponse = responseById.get(responseId);
        if (!sourceResponse) continue;

        const membersById = new Map(members.map((member) => [member.id, member]));
        const ordered = panels
            .map((panel, index) => ({ panel, index, position: sourceRowPosition(panel, membersById, sourceResponse) }))
            .sort((a, b) => (a.position === b.position ? a.index - b.index : a.position < b.position ? -1 : 1))
            .map(({ panel }) => panel);
        returnResults[linkId] = [...ordered, ...members];
    }
};

// Entries per Observation transaction. Big forms are sent in pieces this size (b9a3619743) rather
// than as one transaction; each piece is posted separately.
const OBSERVATION_TRANSACTION_SIZE = 40;

// An Observation's id is worked out from what it records — the form, the item, which occurrence
// of it, and which answer — so recording the same form again writes the same records rather than
// a second copy of each. Every save path gets that from here, whatever it does before or after.
const OBSERVATION_ID_NAMESPACE = '6f1c2b1e-4c3d-5a8b-9e2f-0d7a1b3c5e8f';
const observationId = (...parts) => uuidv5(parts.join('|'), OBSERVATION_ID_NAMESPACE);

// extract QuestionnaireResponse to observations
const extractQuestionnaireResponse = async function (data, existingTransaction = false) {
    let transaction = existingTransaction || [];
    const responseId = data.questionnaireResponse.id;
    const writtenIds = new Set();
    const occurrences = new Map();
    const nextOccurrence = (key) => {
        const occurrence = occurrences.get(key) ?? 0;
        occurrences.set(key, occurrence + 1);
        return occurrence;
    };

    let baseUrl = getUrlFromCanonical(data.questionnaireResponse.questionnaire).fullUrl;
    let questionnaire = (await $httpFhirApi.get(baseUrl + '&_elements=' + EXTRACTION_ELEMENTS)).data[0];

    const effective = new Date().toISOString();

    const disabledIds = data.disabledItems?.filter((e) => typeof e === 'string') || [];
    const disabledObjects = data.disabledItems?.filter((e) => typeof e !== 'string') || [];
    let valueTypes = ['Quantity', 'CodeableConcept', 'string', 'boolean', 'integer', 'Range', 'Ratio', 'SampledData', 'time', 'dateTime', 'Period', 'Attachment'];

    const removeDuplicates = (duplicates) => {
        const flag = {};
        const unique = [];
        duplicates.forEach((elem) => {
            if (!flag[elem.bnValue]) {
                flag[elem.bnValue] = true;
                unique.push(elem);
            }
        });

        return unique;
    };
    const addObservations = (item, qItem, qResponse, parentKey = responseId) => {
        let disabledItemsArray = [...disabledIds];
        if (disabledItemsArray?.includes(item.linkId)) return;
        let observations = [];
        const itemKey = parentKey + '|' + item.linkId + '|' + nextOccurrence(parentKey + '|' + item.linkId);

        // If this is a boolean type field and there is no value (false)
        // insert the boolean: false so that it is stored and can be used
        // when getting the last stored value.
        if (qItem.type === 'boolean') {
            let currentValue = item.answer?.[0]?.value;
            if (!currentValue) {
                item.answer = { value: { boolean: false } };
            }
        }

        // grab only unique values so duplicates are removed
        // for some reason duplicates can be created.  So filter those here
        let itemAnswers = removeDuplicates(item.answer);
        for (const [answerIndex, answerVal] of itemAnswers.entries()) {
            let answerValue = answerVal?.renderingXhtml ? { string: answerVal.renderingXhtml } : answerVal.value;
            let performer = item.auditUser || qResponse.author;

            // answer needs to have a value as well as be of a type that observation supports
            if ((qItem.code.length || qItem.linkId.includes('score-')) && answerValue && valueTypes.some((type) => (type.toString() === 'boolean' ? true : answerValue[type.toString()]))) {
                let scoreCode = [{ system: 'http://fhir.bestnotes.com/scoreField', code: qItem.linkId }];
                let observation = new Observation({
                    id: observationId(itemKey, answerIndex),
                    status: 'final',
                    subject: qResponse.subject,
                    derivedFrom: [getReference(data.questionnaireResponse)],
                    encounter: qResponse.encounter,
                    code: {
                        coding: qItem.code?.length ? qItem.code : scoreCode,
                    },
                    effective: {
                        instant: effective,
                    },
                    value: answerValue,
                    performer: [performer],
                });

                transaction.push(crud.put(observation));
                writtenIds.add(observation.id);
                observations.push(observation.id);
            }
        }

        return observations;
    };

    let count = 0;
    let transactionList = [];
    loopItems(data.questionnaireResponse.item, questionnaire, (item, isQuestion = false) => {
        let qItem = findQuestionnaireItemByLinkId(questionnaire, item.linkId);

        // If a field set.  Loop over items and track observations, so we can set hasMember of main observation
        if (isQuestion) {
            let observations = [];
            // Each row of a field set is its own response item, in the order it was entered.
            const rowIndex = nextOccurrence(responseId + '|row|' + item.linkId);
            const panelId = observationId(responseId, item.linkId, 'row', rowIndex);

            loopItems(item.item, questionnaire, (item2) => {
                let disabledObj = disabledObjects?.find((e) => e.linkId === item2.linkId);
                if (disabledObj) {
                    if (count === disabledObj.index) {
                        return;
                    }
                    count++;
                }
                let qItem2 = findQuestionnaireItemByLinkId(questionnaire, item2.linkId);
                observations = observations.concat(addObservations(item2, qItem2, data.questionnaireResponse, panelId));
            });

            let members = observations.filter((id) => id).map((id) => ({ id, resourceType: 'Observation' }));

            if (members.length) {
                let observation = new Observation({
                    id: panelId,
                    identifier: [{ system: FIELD_SET_ROW_SYSTEM, value: String(rowIndex) }],
                    status: 'final',
                    subject: data.questionnaireResponse.subject,
                    derivedFrom: [getReference(data.questionnaireResponse)],
                    encounter: data.questionnaireResponse.encounter,
                    hasMember: members,
                    code: {
                        coding: qItem.code,
                    },
                    effective: {
                        instant: effective,
                    },
                    performer: [data.questionnaireResponse.author],
                });

                transaction.push(crud.put(observation));
                writtenIds.add(panelId);
            }
        } else {
            // regular item,  Loop through answers and create observations
            addObservations(item, qItem, data.questionnaireResponse);
        }

        if (transaction.length > OBSERVATION_TRANSACTION_SIZE) {
            transactionList.push(transaction);
            transaction = [];
        }
    });

    // Whatever the form recorded before and did not record now is gone from the form — an answer
    // cleared, a row removed — or was written before ids were worked out from what they record.
    let recordedIds = [];
    try {
        recordedIds = await observationIdsFor(data.questionnaireResponse);
    } catch (error) {
        // The new records are still written in place; only the ones this save no longer makes stay.
        console.error('Observations: could not read what the form recorded before, so nothing it no longer records was removed.', { questionnaireResponseId: responseId, error });
    }
    for (const id of recordedIds) {
        if (writtenIds.has(id)) continue;
        transaction.push(crud.delete({ resourceType: 'Observation', id }));
        if (transaction.length > OBSERVATION_TRANSACTION_SIZE) {
            transactionList.push(transaction);
            transaction = [];
        }
    }

    transactionList.push(transaction);

    if (existingTransaction === false) {
        return await Promise.all(transactionList.map((transaction) => postTransaction(transaction)));
    }

    return transactionList;
};

// A large form records well over the server's default page of Observations, and unlocking one
// that left some behind would have its next lock record them all again beside the leftovers.
const OBSERVATION_REMOVAL_PAGE_SIZE = 500;
const OBSERVATION_REMOVAL_MAX_PAGES = 20;

/** Every Observation matching the form's query, across as many pages as that takes. */
async function fetchObservationsFor(qResponse, elements) {
    // A staff note can have no subject; derived-from alone names the form.
    const subject = qResponse.subject?.id ? 'subject=' + qResponse.subject.id + '&' : '';
    const query = '/Observation?' + subject + 'derived-from=' + qResponse.id + '&_elements=' + elements + '&_count=' + OBSERVATION_REMOVAL_PAGE_SIZE;
    const results = [];

    for (let page = 1; page <= OBSERVATION_REMOVAL_MAX_PAGES; page++) {
        const observations = (await $httpFhirApi.get(query + '&_page=' + page))?.data || [];
        results.push(...observations);
        if (observations.length < OBSERVATION_REMOVAL_PAGE_SIZE) break;
    }

    return results;
}

/** Every Observation id a form has recorded, across as many pages as that takes. */
async function observationIdsFor(qResponse) {
    return (await fetchObservationsFor(qResponse, 'id')).map((observation) => observation.id).filter(Boolean);
}

const removeObservations = async function (qResponse, existingTransaction = false) {
    let transaction = existingTransaction || [];

    // Vital signs recorded from the note are clinical data and outlive unlocking or deleting it.
    const { isVitalSign } = useVitalsFlow();
    const observations = await fetchObservationsFor(qResponse, 'id,category');
    observations.filter((observation) => observation?.id && !isVitalSign(observation)).forEach((observation) => transaction.push(crud.delete({ resourceType: 'Observation', id: observation.id })));

    return transaction;
};

const status = async function (data, existingTransaction = false) {
    let transaction = existingTransaction || [];
    // set status
    data.questionnaireResponse.status = data.status;
    // remove signatures if required
    if (data.status === 'in-progress') {
        data.questionnaireResponse.reviewer?.forEach((item) => {
            item.when = undefined;
            item.context = undefined;
            item.format = undefined;
            item.signature = undefined;
            item.ip = undefined;
            item.user = undefined;
            item.credentials = undefined;
            item.jobTitle = undefined;
        });
    }
    // patch questionnaireResponse
    transaction.push(crud.patch(data.questionnaireResponse));

    // determine if this status change should propagate to related tasks
    if (['in-progress', 'completed'].includes(data.status)) {
        // update status of associated tasks
        const collaboratorTasks = data.assignments?.filter((task) => ['author', 'editor', 'viewer'].includes(task.code.coding[0].version)) || [];
        const groupParentTasks = data.assignments?.filter((task) => ['form'].includes(task.code.coding[0].code)) || [];
        // because when we unlock and lock a form, we change the status for all subtasks (patients) of the group note
        // we can assume we can change the status for the parent overall task for the whole form.
        [data.task, ...collaboratorTasks, ...groupParentTasks]
            .filter((task) => task)
            .forEach((task) => {
                task.status = data.status;
                if (data.status === 'completed') {
                    if (!task.executionPeriod) {
                        task.executionPeriod = { end: '' };
                    }
                    task.executionPeriod.end = new Date().toISOString();
                } else if (data.status === 'in-progress') {
                    // reset executionPeriod
                    if (!task.executionPeriod) {
                        task.executionPeriod = { start: new Date().toISOString() };
                    } else task.executionPeriod.end = undefined;
                }
                transaction.push(crud.patch(task));
            });
    }

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
};

const signerReviewer = async function (data, existingTransaction = false) {
    let transaction = existingTransaction || [];

    // A response can legitimately have no signers — an ROI whose authority rows could not be read
    // seeds none — and toJSON drops the empty array, so the copy arrives with no reviewer at all.
    if (data.questionnaireResponse && !data.questionnaireResponse.reviewer) data.questionnaireResponse.reviewer = [];

    let isLockedQuestionnaire = data.questionnaireResponse?.status === 'completed';

    // assign tasks when form is locked
    if (data.action === 'assign') {
        data.questionnaireResponse.reviewer.forEach((item) => {
            const action = item.type === 'sign' ? 'signature' : 'review';
            // Skip if an assignment task already exists for this owner+action.
            // Defends against duplicate task creation when lock() runs twice
            // (e.g. a stale invocation overlaps with a fresh one).
            const existing = data.assignments?.find((t) => t.owner?.id === item.who?.id && t.code?.coding?.[0]?.version === action);
            if (existing) return;
            const assignment = populateSignerReviewerTask(data.task, item.who, action);
            // Stamp an assignment done rather than opening a pending request against a reviewer
            // whose signature is already on the response.
            if (item.when) {
                assignment.status = 'completed';
                assignment.executionPeriod = { end: item.when };
            }
            transaction.push(crud.post(assignment));
        });
    }

    // update signer/reviewer list and corresponding tasks
    else if (data.action === 'update') {
        /**
         * We want to be sure that a reviewer can only exist once as either a 'Signer' or 'Reviewer'
         * Replace/Update changed reviewers
         * Add new reviewers
         * Removal of orphaned users/tasks will happen later
         */

        data.reviewer.forEach((newReviewer) => {
            // remove reviewer and corresponding task if they already exist for another type
            const index = data.questionnaireResponse.reviewer.findIndex((oldReviewer) => newReviewer.who.id === oldReviewer.who.id && newReviewer.type !== oldReviewer.type);
            if (index > -1) {
                data.questionnaireResponse.reviewer.splice(index, 1);
                const oldType = newReviewer.type === 'sign' ? 'review' : 'signature';
                const task = data.assignments.find((task) => task.owner.id === newReviewer.who.id && oldType === task.code.coding[0].version);
                if (task) transaction.push(crud.delete(task));
            }
            // add/update reviewer and task if not already set for this type
            if (!data.questionnaireResponse.reviewer.find((oldReviewer) => newReviewer.who.id === oldReviewer.who.id && newReviewer.type === oldReviewer.type)) {
                data.questionnaireResponse.reviewer.push(newReviewer);
                const newType = newReviewer.type === 'sign' ? 'signature' : 'review';
                if (isLockedQuestionnaire) transaction.push(crud.post(populateSignerReviewerTask(data.task, newReviewer.who, newType)));
            }
        });

        /**
         * Now we clean up any orphaned reviewers (not in the new list)
         * including the associated tasks
         */
        data.questionnaireResponse.reviewer = data.questionnaireResponse.reviewer.filter((oldReviewer) => {
            const activeReviewer = !!data.reviewer.find((newReviewer) => newReviewer.who.id === oldReviewer.who.id);
            if (!activeReviewer) {
                const task = data.assignments.find((task) => task.owner.id === oldReviewer.who.id && ['signature', 'review'].includes(task.code.coding[0].version));
                if (task) transaction.push(crud.delete(task));
            }
            return activeReviewer;
        });

        // patch questionnaireResponse with updated signers/reviewers
        transaction.push(crud.patch(data.questionnaireResponse));
        data.questionnaireResponse.setObjectSaved();
    }

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
};

const removeAssignments = async function (data, existingTransaction = false) {
    let transaction = existingTransaction || [];
    const tasks = data.assignments?.filter((task) => data.type.includes(task.code.coding[0]?.version)) || [];
    tasks.forEach((task) => {
        transaction.push(crud.delete(task));
    });

    return transaction;
};

// Deletes ChargeItems and authorization tracking for a QuestionnaireResponse
const cleanupChargeItemsForQuestionnaireResponse = async (questionnaireResponseId, encounterId = null, existingTransaction = false, isGroupEncounter = false) => {
    let transaction = existingTransaction || [];

    const { removeFormChargeItems } = useChargeItemFlow();
    const { removeAuthTrackingForChargeItems } = useAuthFlow();

    // Check if there are Claims referencing this form before deciding what to delete
    let hasSubmittedClaims = false;
    let encountersWithClaims = new Set(); // Track which encounters have claims for group encounters

    // Use encounter-based claim search if available
    if (encounterId) {
        let encounterIdsToCheck = [encounterId];

        // For group encounters, also check parent encounters
        if (isGroupEncounter) {
            const allEncounterIds = await getGroupEncounterIds(encounterId, []);
            encounterIdsToCheck = allEncounterIds;
        }

        // Check all encounters for claims - logic differs for group vs individual
        for (const encounterIdToCheck of encounterIdsToCheck) {
            const claimResponse = await $httpFhirApi.get(`Claim?encounter=${encounterIdToCheck}`);
            const claims = claimResponse.data?.entry?.map((e) => e.resource) || claimResponse.data || [];

            // Check if any claims reference this form via supportingInfo
            const hasClaimsForThisForm = hasClaimsForForm(claims, questionnaireResponseId);

            if (hasClaimsForThisForm) {
                if (isGroupEncounter) {
                    encountersWithClaims.add(encounterIdToCheck);
                }
                hasSubmittedClaims = true;

                // For individual encounters, we can break early since any claim means skip all cleanup
                if (!isGroupEncounter) {
                    break;
                }
            }
        }
    } else {
        hasSubmittedClaims = false;
    }

    // For individual encounters with claims, skip all cleanup
    // For group encounters, we'll handle cleanup per encounter below
    if (!isGroupEncounter && hasSubmittedClaims) {
        return transaction;
    }

    // Search for ChargeItems that reference our specific form
    let chargeItems = [];
    const chargeItemResponse = await $httpFhirApi.get(`ChargeItem?supportingInformation=QuestionnaireResponse/${questionnaireResponseId}`);
    chargeItems = chargeItemResponse.data?.entry?.map((e) => e.resource) || chargeItemResponse.data || [];

    if (chargeItems.length > 0) {
        // Determine which charge items to delete based on encounter type and claim status
        let chargeItemsToDelete = chargeItems;
        if (isGroupEncounter && hasSubmittedClaims) {
            // For group encounters with claims: only delete charge items for encounters without claims
            chargeItemsToDelete = chargeItems.filter((chargeItem) => {
                const chargeItemEncounterId = chargeItem.context?.id;
                return !chargeItemEncounterId || !encountersWithClaims.has(chargeItemEncounterId);
            });
        }

        if (chargeItemsToDelete.length > 0) {
            // Get the charge item and patient IDs of ChargeItems to be deleted
            const chargeItemIds = chargeItemsToDelete.map((ci) => ci.id);
            const patientIdsFromChargeItems = chargeItemsToDelete.map((ci) => ci.subject?.id || ci.subject?.reference?.split('/').pop()).filter(Boolean);

            if (patientIdsFromChargeItems.length > 0) {
                // Remove duplicates and join with commas for group encounters
                const uniquePatientIds = [...new Set(patientIdsFromChargeItems)];
                const patientId = uniquePatientIds.join(',');

                // Remove authorization tracking for these ChargeItems
                transaction = await removeAuthTrackingForChargeItems(chargeItemIds, patientId, transaction);
            }

            // Remove the ChargeItems themselves
            transaction = await removeFormChargeItems(chargeItemIds, transaction);
        }
    }
    return transaction;
};

// Resolve the per-diem applicable procedure codes for the active encounter's enrolling
// program by following the partOf chain. Returns an empty Set when the enrolling program
// is not per-diem — a stray perDiemProcedure reference on a non-per-diem program must NOT
// suppress per-session charges (#4728). Shares resolvePerDiemApplicableCodes with the
// covered-charges table suppression (perDiemSuppression.js) for a single source of truth.
export const findPerDiemApplicableCodesFromEncounters = async (data) => {
    if (!data.activeEncounter?.id) return new Set();

    try {
        // Include the whole partOf chain plus the enrolling program and the per-diem service it references.
        const encounterResponse = await $httpFhirApi.get(
            `Encounter?id=${data.activeEncounter.id}&_include:recurse=Encounter:part-of&_include=Encounter:healthcareService&_include:iterate=HealthcareService:perDiemProcedure`,
        );
        const resources = encounterResponse.data || [];

        // Index HealthcareServices so the shared resolver can follow perDiemProcedure references.
        const hsById = new Map();
        for (const item of resources) {
            if (item?.resourceType === 'HealthcareService' && item?.id) hsById.set(item.id, item);
        }

        // Use the first per-diem enrolling program reached on the chain (resolver returns
        // an empty Set if there is none, or if it is not isPerDiem).
        const perDiemProgram = resources.find((item) => item?.resourceType === 'HealthcareService' && item?.isProgram === true && item?.isPerDiem === true);

        return resolvePerDiemApplicableCodes(perDiemProgram, hsById);
    } catch (error) {
        console.error('Error fetching encounter partOf chain:', error);
        return new Set();
    }
};

// function to check if session is associated with per-diem program and has per-diem procedure codes
export async function shouldSkipChargeItemProcessingForPerDiem(data) {
    const procedureCodes = new Set();

    // Add codes from fee schedule data (which reference HealthcareServices by billingCode)
    if (data.feeScheduleData?.length) {
        data.feeScheduleData.forEach((feeSchedule) => {
            if (feeSchedule.charges?.length) {
                feeSchedule.charges.forEach((charge) => {
                    if (charge.billingCode) {
                        procedureCodes.add(charge.billingCode);
                    }
                });
            }
        });
    }

    // Add codes from updated services (HealthcareServices from charge master)
    if (data.updatedServices?.length) {
        data.updatedServices.forEach((service) => {
            if (service.billingCode) {
                procedureCodes.add(service.billingCode);
            }
        });
    }

    // Resolve the enrolling program's per-diem applicable codes (empty unless the program
    // is isPerDiem === true — see resolvePerDiemApplicableCodes).
    const applicableCodes = await findPerDiemApplicableCodesFromEncounters(data);
    if (!applicableCodes.size) return false;

    // Skip per-session charge creation when a session code is rolled up into the per-diem bill.
    return Array.from(procedureCodes).some((code) => applicableCodes.has(code));
}

// Helper function to update procedure status when form is completed
const completeProceduresForEncounter = async function (encounterId, existingTransaction = false) {
    let transaction = existingTransaction || [];

    if (!encounterId) {
        return transaction;
    }

    try {
        // Find procedures associated with this encounter that are still in-progress
        const procedureResponse = await $httpFhirApi.get(`Procedure?encounter=${encounterId}&status=in-progress`);
        const procedures = procedureResponse.data?.entry?.map((e) => e.resource) || procedureResponse.data || [];

        // Update each procedure to completed status
        procedures.forEach((procedure) => {
            procedure.status = 'completed';
            transaction.push(crud.patch(procedure));
        });
    } catch (error) {
        console.error('Error updating procedure status:', error);
    }

    return transaction;
};

// The roi item answer carries the response's consents as JSON, one object or an array of them.
// Unparseable reads as none, so a bad answer never newly blocks lock() or signReview().
const parseRoiConsents = (roiItem) => {
    const answer = roiItem?.answer?.[0]?.value?.string;
    if (!answer) return [];

    try {
        const parsed = JSON.parse(answer);
        return Array.isArray(parsed) ? parsed : [parsed];
    } catch (error) {
        // Reading as none is deliberate, but a corrupt answer would otherwise leave no trace at all.
        console.error('[parseRoiConsents] unparseable roi answer; treating the response as recording no consents', error);
        return [];
    }
};

// Every authorizer of record across those consents. Legacy ROIs record none, which is what lets
// their empty reviewer list pass.
const roiGrantorIds = (roiItem) => parseRoiConsents(roiItem).flatMap((consent) => (consent?.grantor || []).map((grantor) => grantor?.id).filter(Boolean));

// An ROI's consents may activate only once every required signature is in place AND every authorizer
// of record is among those who signed. Zero 'sign' reviewers satisfies that only for legacy responses
// recording no grantor; 'review' reviewers never block activation.
export const roiSignaturesSatisfied = (questionnaireResponse) => {
    const roiItem = questionnaireResponse?.item?.find((item) => item.linkId === 'roi');
    if (!roiItem) return false;

    const grantorIds = roiGrantorIds(roiItem);
    const signers = questionnaireResponse.reviewer?.filter((r) => r.type === 'sign') || [];
    if (!signers.length) return !grantorIds.length;
    if (!signers.every((signer) => signer.signature && signer.when)) return false;

    // Taking the authorizer off the Signers dialog must not let the release activate on whoever is
    // left signing in their place.
    return grantorIds.every((id) => signers.some((signer) => signer.who?.id === id));
};

// Why a fully signed release is not activating, or null while signing is under way or nothing listed
// is Pending. Without it a release left Pending after everyone signed looks like one awaiting a signer.
const pendingRoiBlock = (questionnaireResponse, roiItem) => {
    const signers = questionnaireResponse.reviewer?.filter((r) => r.type === 'sign') || [];
    if (!signers.length || !signers.every((signer) => signer.when)) return null;
    if (!roiItem.answer?.[0]?.value?.string) {
        return { reason: 'roi answer is empty', message: 'This release stays Pending: the form records no release to activate. Contact support.', consentIds: [] };
    }
    // A repeat signature re-checks an already Active release, which is not stuck.
    const consentIds = parseRoiConsents(roiItem)
        .filter((consent) => consent?.id && consent.status === 'proposed')
        .map((consent) => consent.id);
    if (!consentIds.length) return null;
    if (signers.some((signer) => !signer.signature)) {
        return { reason: 'a stamped signer has no signature value', message: 'This release stays Pending: a signature was not recorded. Contact support.', consentIds };
    }
    return { reason: 'an authorizer of record has not signed', message: 'This release stays Pending: the person authorizing it has not signed. Add them to the Signers list.', consentIds };
};

const updateConsentStatus = async function (questionnaireResponse, newStatus, existingTransaction = false, trigger = newStatus === 'active' ? 'sign' : 'unlock') {
    let transaction = existingTransaction || [];
    let idsToFetch = [];

    // The trigger names what did succeed: lock and unlock record no signature, so saying one was saved would mislead.
    // A partial failure names the count, so the user isn't told every release failed when some moved.
    const failedMessage = (missing, total) => {
        const noun = total > 1 ? 'releases' : 'release';
        const partial = missing < total ? `${missing} of ${total} ${noun}` : null;
        const succeeded = { sign: 'The signature was saved', lock: 'The form was locked', unlock: 'The form was unlocked' }[trigger];
        return newStatus === 'active'
            ? `${succeeded}, but ${partial || `the ${noun}`} could not be activated.`
            : `${succeeded}, but ${partial || `its ${noun}`} could not be returned to Pending. Contact support.`;
    };
    const notifyStatusFailed = (missing, total) => useNotificationStore().add({ message: failedMessage(missing, total), color: 'error' });

    try {
        const roiItem = questionnaireResponse.item?.find((item) => item.linkId === 'roi');
        const blocked = !roiItem?.answer?.[0]?.value?.string || (newStatus === 'active' && !roiSignaturesSatisfied(questionnaireResponse));
        if (blocked) {
            const block = roiItem && newStatus === 'active' ? pendingRoiBlock(questionnaireResponse, roiItem) : null;
            if (block) {
                console.warn(`[updateConsentStatus] ${block.reason}; Consent ${block.consentIds.join(',') || '(none listed)'} stays proposed on QuestionnaireResponse/${questionnaireResponse.id}`);
                useNotificationStore().add({ message: block.message, color: 'warning' });
            }
            return transaction;
        }

        const consentArray = parseRoiConsents(roiItem);

        // Batch-fetch all consents that need updating in one call
        idsToFetch = consentArray
            .filter((c) => {
                if (!c.id) return false;
                return (newStatus === 'active' && c.status === 'proposed') || (newStatus === 'proposed' && c.status === 'active');
            })
            .map((c) => c.id);

        if (!idsToFetch.length) return transaction;

        // The portal may only read a Consent through a query naming its patient — a bare _id search is
        // refused with a 403, and the release then never leaves Pending. The response's subject is
        // that patient, so scoping by it returns the same rows on either side.
        const patientScope = questionnaireResponse.subject?.id ? `&patient=${questionnaireResponse.subject.id}` : '';
        const response = await $httpFhirApi.get(`Consent?_id=${idsToFetch.join(',')}${patientScope}&_elements=Consent.status`);
        const consents = Array.isArray(response.data) ? response.data : response.data?.entry?.map((e) => e.resource) || [];
        const consentMap = new Map();
        consents.forEach((consent) => {
            if (consent.id) consentMap.set(consent.id, consent);
        });

        const missingIds = idsToFetch.filter((id) => !consentMap.has(id));
        if (missingIds.length) {
            console.error(
                `[updateConsentStatus] Consent read returned ${consentMap.size} of ${idsToFetch.length}; Consent ${missingIds.join(',')} not set to ${newStatus} for QuestionnaireResponse/${questionnaireResponse.id}`,
            );
            notifyStatusFailed(missingIds.length, idsToFetch.length);
        }

        // The form's copy can be stale: revoking on a locked form updates the Consent but not the copy,
        // so the fetched status decides. Otherwise unlocking would turn a revoked release back to Pending.
        const fromStatus = newStatus === 'active' ? 'proposed' : 'active';
        for (const consentData of consentArray) {
            if (consentData.id && idsToFetch.includes(consentData.id)) {
                const consent = consentMap.get(consentData.id);
                if (!consent) continue;
                if (consent.status !== fromStatus) {
                    consentData.status = consent.status;
                    continue;
                }
                consent.status = newStatus;
                transaction.push(crud.patch(consent));
                consentData.status = newStatus;
            }
        }

        // Update the questionnaireResponse with the modified consent data
        roiItem.answer[0].value.string = JSON.stringify(consentArray);
        transaction.push(crud.patch(questionnaireResponse));
    } catch (error) {
        // The caller's own QR patch is already in the transaction, so returning it still commits the signature.
        console.error(`[updateConsentStatus] could not set Consent ${idsToFetch.join(',')} to ${newStatus} for QuestionnaireResponse/${questionnaireResponse.id}`, error);
        notifyStatusFailed(idsToFetch.length, idsToFetch.length);
    }

    return transaction;
};

// Main methods
async function lock(data, existingTransaction = false) {
    const isPortal = data.isPortal || false;
    let mainTransaction = existingTransaction || [];
    let observationTransactions = [];

    if (data.questionnaireResponse) {
        data.questionnaireResponses = [data.questionnaireResponse];
    }

    const taskResults = await Promise.all(data.questionnaireResponses.map((qr) => getTasksForQuestionnaireResponse(qr.id)));

    let procedurePromises = [];

    for (let i = 0; i < data.questionnaireResponses.length; i++) {
        const questionnaireResponse = data.questionnaireResponses[i];
        const [task, assignments] = taskResults[i];
        // use copy of QuestionnaireResponse
        const qr = questionnaireResponse.getObjectCopy();

        // Lock form/questionnaireResponse
        mainTransaction = await status(
            {
                status: 'completed',
                questionnaireResponse: qr,
                user: data.user,
                task: task,
                assignments: assignments,
            },
            mainTransaction,
        );

        mainTransaction = await signerReviewer(
            {
                action: 'assign',
                task: task,
                assignments: assignments,
                questionnaireResponse: qr,
            },
            mainTransaction,
        );

        // this is commented out because it causes issues with assigning multiple signer tasks.
        // mainTransaction = uniqBy(mainTransaction, 'request.url');

        // ROI consents whose signature requirements are already satisfied (including
        // legacy responses with no signers at all — see #5105) activate on lock;
        // signReview only runs when there is a signer left to stamp, so this is the
        // only path that activates a zero-signer ROI.
        if (roiSignaturesSatisfied(qr)) {
            mainTransaction = await updateConsentStatus(qr, 'active', mainTransaction, 'lock');
            // updateConsentStatus patches the QR again (consent statuses are rewritten
            // into the roi answer), duplicating the patch pushed by status(). Keep only
            // the LAST QuestionnaireResponse patch per URL — crud.patch diffs at call
            // time, so the last entry is the superset. Scoped to QR patches because the
            // blanket uniqBy above broke multi-signer task assignment.
            const lastQrPatchIndex = new Map();
            mainTransaction.forEach((entry, index) => {
                if (entry?.request?.method === 'PATCH' && entry.request.url.startsWith('/QuestionnaireResponse/')) {
                    lastQrPatchIndex.set(entry.request.url, index);
                }
            });
            mainTransaction = mainTransaction.filter((entry, index) => {
                if (!(entry?.request?.method === 'PATCH' && entry.request.url.startsWith('/QuestionnaireResponse/'))) return true;
                return lastQrPatchIndex.get(entry.request.url) === index;
            });
        }

        let transactions = await extractQuestionnaireResponse(
            {
                questionnaireResponse: qr,
                disabledItems: data.disabledItems,
            },
            [],
        );

        observationTransactions = [...observationTransactions, ...transactions];

        // Update procedure status to completed when form is finished
        const encounterId = qr.encounter?.id;
        if (encounterId && !isPortal) {
            procedurePromises.push(completeProceduresForEncounter(encounterId, []));
        }
    }

    if ((data.patients || data.activePatient) && !isPortal) {
        const qrsWithEncounters = data.questionnaireResponses.filter((qr) => qr.encounter?.id);

        const uniqueEncounterIds = [...new Set(qrsWithEncounters.map((qr) => qr.encounter.id))];
        const getEncounterIds = data.isGroupEncounter ? getGroupEncounterIds : getIndividualEncounterIds;
        const encounterIdResults = await Promise.all(uniqueEncounterIds.map((id) => getEncounterIds(id, [])));
        const allEncounterIds = [...new Set(encounterIdResults.flat())];

        let questionnaireResponsesWithoutClaims = qrsWithEncounters;
        if (allEncounterIds.length) {
            const claims = (await $httpFhirApi.get(`Claim?encounter=${allEncounterIds.join(',')}&_elements=status,supportingInfo`)).data;

            const referencedFormIds = new Set(
                claims
                    .filter((claim) => claim.status === 'active')
                    .flatMap((claim) => claim.supportingInfo || [])
                    .map((info) => info.code?.text?.match(/QuestionnaireResponse\/([^/\s]+)/)?.[1])
                    .filter(Boolean),
            );
            questionnaireResponsesWithoutClaims = qrsWithEncounters.filter((qr) => !referencedFormIds.has(qr.id));
        }

        // Process all questionnaire responses without claims
        if (questionnaireResponsesWithoutClaims.length > 0) {
            const shouldSkipChargeProcessing = await shouldSkipChargeItemProcessingForPerDiem(data);

            if (!shouldSkipChargeProcessing) {
                const { processChargeItemFlow } = useChargeItemFlow();
                await processChargeItemFlow({
                    ...data,
                    questionnaireResponses: questionnaireResponsesWithoutClaims,
                });
            }
        }
    }

    const procedureTransactions = await Promise.all(procedurePromises);
    mainTransaction = [...mainTransaction, ...procedureTransactions.flat()];

    if (existingTransaction === false) {
        return await Promise.all([postTransaction(mainTransaction), ...observationTransactions.map((transaction) => postTransaction(transaction))]);
    }

    return [];
}

async function unlock(data, existingTransaction = false) {
    let transaction = existingTransaction || [];

    if (data.questionnaireResponse) {
        data.questionnaireResponses = [data.questionnaireResponse];
    }

    for (let questionnaireResponse of data.questionnaireResponses) {
        // fetch associated tasks
        let [task, assignments] = await getTasksForQuestionnaireResponse(questionnaireResponse.id);

        // use copy of QuestionnaireResponse
        const qr = questionnaireResponse.getObjectCopy();

        transaction = await status(
            {
                status: 'in-progress',
                questionnaireResponse: qr,
                user: data.user,
                task: task,
                assignments: assignments,
            },
            transaction,
        );

        transaction = await removeAssignments(
            {
                assignments: assignments,
                type: ['signature', 'review'],
            },
            transaction,
        );

        transaction = await removeObservations(qr, transaction);

        const encounterId = questionnaireResponse.encounter?.id;

        // Clean up charge items
        const isGroupEncounter = encounterId ? !!task.groupTask : false;
        transaction = await cleanupChargeItemsForQuestionnaireResponse(questionnaireResponse.id, encounterId, transaction, isGroupEncounter);

        // Update consent status to proposed when ROI form is unlocked
        transaction = await updateConsentStatus(qr, 'proposed', transaction);
    }

    transaction = uniqBy(transaction, 'request.url');

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
}

async function unlockSignatureReview(data, existingTransaction = false) {
    let transaction = existingTransaction || [];

    if (data.questionnaireResponse) {
        data.questionnaireResponses = [data.questionnaireResponse];
    }

    for (let questionnaireResponse of data.questionnaireResponses) {
        let [task, assignments] = await getTasksForQuestionnaireResponse(questionnaireResponse.id);

        const hasSignatureOrReviewAssignments = assignments.some((assignment) => assignment.code.coding.some((coding) => coding.version === 'signature' || coding.version === 'review'));

        // If the response has signers/reviewers, remove them and assign new tasks
        if (hasSignatureOrReviewAssignments) {
            transaction = await removeAssignments(
                {
                    assignments: assignments,
                    type: ['signature', 'review'],
                },
                transaction,
            );

            transaction = uniqBy(transaction, 'request.url');

            transaction = await signerReviewer(
                {
                    action: 'assign',
                    task: task,
                    questionnaireResponse: questionnaireResponse.getObjectCopy(),
                },
                transaction,
            );
        }
    }

    if (existingTransaction === false) {
        await postTransaction(transaction);
    }

    return transaction;
}

async function updateSignerReviewer(data, existingTransaction = false) {
    let transaction = existingTransaction || [];

    let questionnaireResponses = await $httpFhirApi.get('/QuestionnaireResponse?id=' + data.qrIds.join(','));
    // Each copy ends up holding its response's signer list exactly as this update writes it, so a
    // caller holding its own copies adopts the list from here instead of recomputing it (#6256).
    data.updatedQuestionnaireResponses = [];

    // do same thing for all QRs @TODO later change this to be patient specific when we add patient signing to group notes
    for (let questionnaireResponse of questionnaireResponses.data) {
        // fetch associated tasks
        let [task, assignments] = await getTasksForQuestionnaireResponse(questionnaireResponse.id);
        // use copy of QuestionnaireResponse
        const qr = questionnaireResponse.getObjectCopy();
        data.updatedQuestionnaireResponses.push(qr);

        // For patients, we want to make sure reviewers are tied to the right QuestionnaireResponse
        let reviewers = data.reviewers.filter((item) => item.reviewer.who?.resourceType !== 'Patient' || [item.relatedTo, item.reviewer.who?.id].includes(qr.subject?.id));

        transaction = await signerReviewer(
            {
                action: 'update',
                task: task,
                assignments: assignments,
                questionnaireResponse: qr,
                reviewer: reviewers.map((item) => item.reviewer),
            },
            transaction,
        );
    }

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
}

const REVIEWER_TYPE_FOR_ACTION = { signature: 'sign', review: 'review' };

const signReview = async (data, existingTransaction = false) => {
    let transaction = existingTransaction || [];
    const authStore = useAuthStore();
    let ip = await axios.get('https://api.ipify.org?format=json');

    if (data.questionnaireResponse) {
        data.questionnaireResponses = [data.questionnaireResponse];
    }

    const taskResults = await Promise.all(data.questionnaireResponses.map((qr) => getTasksForQuestionnaireResponse(qr.id)));
    let unmatchedCount = 0;
    let refusedCount = 0;

    for (let i = 0; i < data.questionnaireResponses.length; i++) {
        const qr = data.questionnaireResponses[i];
        const assignments = taskResults[i][1];

        // use copy of QuestionnaireResponse
        const questionnaireResponse = qr.getObjectCopy();

        // The actor may arrive as a resource ({ id }) or as a reviewer-shaped
        // entry ({ who: { id } }) — the portal passes the latter.
        const actorId = data.actor?.id ?? data.actor?.who?.id;

        // A `when` with no signature value reads as signed but can never satisfy activation.
        if (data.action === 'signature' && !data.signatureProps?.signature) {
            console.error(`[signReview] signature action with no signature value on QuestionnaireResponse/${questionnaireResponse.id} for actor ${actorId}; nothing written`);
            refusedCount++;
            continue;
        }

        // Stamp only an entry of the action's type, and never re-stamp a signed one. A sign entry with
        // a `when` but no signature is an erased signature, so it counts as unsigned and is re-stamped.
        const entryType = REVIEWER_TYPE_FOR_ACTION[data.action];
        const matches = questionnaireResponse.reviewer?.filter((item) => item.who?.id === actorId && item.type === entryType) || [];
        const reviewer = entryType === 'sign' ? matches.find((item) => !(item.when && item.signature)) : matches.find((item) => !item.when) || matches[0];
        if (!matches.length) {
            // Warn, report and tell the user, so a signer is never shown success while nothing was stamped.
            console.warn(`[signReview] no reviewer on QuestionnaireResponse/${questionnaireResponse.id} matches actor ${actorId}; signature not recorded`);
            data.unmatched = [...(data.unmatched || []), { questionnaireResponseId: questionnaireResponse.id, actorId }];
            unmatchedCount++;
            continue;
        }

        if (!reviewer) {
            // A repeat signature keeps the first one but still closes the signer's Tasks and re-checks activation.
            console.warn(`[signReview] actor ${actorId} already signed QuestionnaireResponse/${questionnaireResponse.id}; keeping the first signature`);
        } else {
            reviewer.when = timezoneApi.getLocalDateTime(new Date(), "yyyy-MM-dd'T'HH:mm:ss.SSSxxx", Intl.DateTimeFormat().resolvedOptions().timeZone);
            // Freeze the signer's name as of this moment. Callers pass either a resolved
            // signer (display) or a raw resource (fullName); when neither yields a name the
            // stored display is left alone rather than blanked.
            const signerDisplay = data.actor?.display || signerNameFromResource(data.actor) || '';
            if (signerDisplay) reviewer.who.display = signerDisplay;
            if (data.action === 'signature' && data.signatureProps) {
                reviewer.context = data.signatureProps?.context;
                reviewer.format = data.signatureProps?.format;
                reviewer.signature = data.signatureProps?.signature;
                reviewer.ip = ip?.data?.ip;
                reviewer.user = authStore.aidboxPersonReference;
                reviewer.jobTitle = data.signatureProps?.jobTitle;
                reviewer.credentials = data.signatureProps?.credentials;
            }
            transaction.push(crud.patch(questionnaireResponse));
        }

        // update associated task(s) — use filter not find: a double-fire on
        // task creation can leave the same actor with multiple assignment
        // tasks for the same QR/action. Without this, only the first match
        // got transitioned to completed and the duplicate stayed pending,
        // leaving the QR signed but a stale task lingering.
        const tasks = assignments?.filter((task) => task.owner?.id === actorId && task.code?.coding?.[0]?.version === data.action) || [];
        const completedAt = new Date().toISOString();
        for (const task of tasks) {
            task.status = 'completed';
            if (!task.executionPeriod) {
                task.executionPeriod = { end: '' };
            }
            task.executionPeriod.end = completedAt;
            transaction.push(crud.patch(task));
        }

        // After signing, check if all signatures are complete and update consent status if needed
        if (data.action === 'signature') {
            transaction = await updateConsentStatus(questionnaireResponse, 'active', transaction);
        }
    }

    // One message per signing, however many responses a group signing passed.
    if (refusedCount) {
        useNotificationStore().add({ message: 'The signature was not recorded: no signature was provided.', color: 'error' });
    }

    // A group signing passes every participant's response and the actor is on only some of them,
    // so a miss is an error only when the actor matched none.
    if (unmatchedCount && unmatchedCount === data.questionnaireResponses.length) {
        const [noun, list] = data.action === 'review' ? ['review', 'Reviewers'] : ['signature', 'Signers'];
        useNotificationStore().add({ message: `Your ${noun} was not recorded: you are not on the ${list} list for this form.`, color: 'error' });
    }

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
};

// Dedup key for a service about to become a Procedure: the billing code for regular
// services, or the 'non-billable' marker for non-billable ones.
export const serviceProcedureKey = (service) => (service?.id === 'non-billable' ? 'non-billable' : service?.billingCode);

// Collects the procedure codes that already exist (as Procedures) for this patient
// anywhere on the session — the participant encounter and its child form encounters.
// Used to prevent duplicate Procedures when a session has multiple forms mapped to the
// same code, or when a form is initialized more than once. See issue #4167.
export const getExistingSessionProcedureCodes = async (patientId, sessionEncounterId) => {
    const codes = new Set();
    if (!patientId || !sessionEncounterId) return codes;
    try {
        const childEncounters = (await $httpFhirApi.get(`Encounter?part-of=${sessionEncounterId}&_elements=id`)).data || [];
        const encounterIds = [sessionEncounterId, ...childEncounters.map((e) => e.id)].filter(Boolean).join(',');
        const existingProcedures = (await $httpFhirApi.get(`Procedure?subject=${patientId}&encounter=${encounterIds}&_elements=code`)).data || [];
        existingProcedures.forEach((proc) => {
            const coding = proc.code?.coding?.[0];
            // non-billable procedures carry only a `version` of 'non-billable'; regular ones carry `code`.
            const key = coding?.code || coding?.version;
            if (key) codes.add(key);
        });
    } catch (err) {
        console.error('[initializeQuestionnaire] failed to load existing session procedures for dedup', err);
    }
    return codes;
};

async function initializeQuestionnaire(data, existingTransaction = false) {
    if (!data?.questionnaireResponse?.id) {
        return existingTransaction || [];
    }

    let transaction = existingTransaction || [];
    // fetch associated tasks
    let subtasks = await getSubtasks(data.task.id);

    // update status of task & assignments
    data.task.status = 'in-progress';
    data.task.focus = { id: data.questionnaireResponse.id, display: data.task.focus?.display, resourceType: 'QuestionnaireResponse' };
    transaction.push(crud.patch(data.task));
    subtasks.forEach((task) => {
        task.status = 'in-progress';
        transaction.push(crud.patch(task));
    });

    let formEncounter;
    let encounterFormID = uuidv4();
    let qrHasSessionWidget = data.questionnaireResponse.item.some((e) => e.linkId === 'encounterDetails');

    if (qrHasSessionWidget && data.encounter) {
        if (!data.encounter) return;

        formEncounter = new Encounter({
            id: encounterFormID,
            status: 'in-progress',
            code: data.encounter.code,
            class: data.encounter.class,
            period: data.encounter.period,
            subject: data.encounter.subject,
            location: data.encounter.location,
            setting: data.encounter.setting,
            diagnosis: data.encounter.diagnosis,
            appointment: data.encounter.appointment,
            participant: data.encounter.participant,
            episodeOfCare: data.encounter.episodeOfCare,
            partOf: { id: data.encounter.id, resourceType: 'Encounter' },
            type: [{ text: 'formEncounter' }],
        });

        transaction.push(crud.post(formEncounter));
        data.questionnaireResponse.encounter = { id: encounterFormID, resourceType: 'Encounter' };
        transaction.push(crud.patch(data.questionnaireResponse));

        // A group/individual session bills one Procedure per code regardless of how many
        // forms reference it. Gather the codes already present on the session so we don't
        // create duplicates across multiple forms or on re-initialization. See issue #4167.
        const existingProcedureCodes = await getExistingSessionProcedureCodes(data.encounter.subject?.id, data.encounter.id);
        const filterNewServices = (services) =>
            (services || []).filter((service) => {
                const key = serviceProcedureKey(service);
                return key ? !existingProcedureCodes.has(key) : true;
            });

        if (data.appointmentType?.id) {
            const appointmentResponse = (await $httpFhirApi.get('/BN_EncounterType?id=' + data.appointmentType?.id + '&_include=service')).data;
            const appointmentType = appointmentResponse.find((r) => r.resourceType === 'BN_EncounterType');

            const currentFormUrl = getUrlFromCanonical(data.questionnaireResponse.questionnaire)?.url;
            const currentFormEntry = appointmentType?.form?.find((e) => e.url === currentFormUrl);

            const appointmentTypeService = currentFormEntry
                ? [currentFormEntry]
                      .map((e) => {
                          if (e.service?.display === 'non-billable') {
                              return {
                                  id: 'non-billable',
                                  code: { coding: [{ display: 'Non-billable', version: 'non-billable' }] },
                                  perUnitDuration: 0,
                              };
                          }
                          if (e.service?.id) {
                              return appointmentResponse.find((r) => r.id === e.service.id);
                          }
                      })
                      .filter(Boolean)
                : [];

            const newServices = filterNewServices(appointmentTypeService);
            if (newServices.length) {
                transaction = await addProcedure(
                    {
                        patient: data.encounter.subject,
                        service: newServices,
                        encounter: formEncounter,
                    },
                    transaction,
                );
            }
        } else if (data.service?.length) {
            const newServices = filterNewServices(data.service);
            if (newServices.length) {
                transaction = await addProcedure(
                    {
                        patient: data.encounter.subject,
                        service: newServices,
                        encounter: formEncounter,
                    },
                    transaction,
                );
            }
        }
    }

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
}
const addProcedure = async (data, existingTransaction = false) => {
    let transaction = existingTransaction || [];

    if (!data.service?.length) return transaction;

    const encounter = data.encounter?.id ? { resourceType: 'Encounter', id: data.encounter.id } : undefined;
    // Handle non-billable services first (id is non-billable because it's AppointmentService)
    const nonBillableServices = data.service.filter((ser) => ser.id === 'non-billable');

    for (const service of nonBillableServices) {
        let resource = new Procedure({
            id: uuidv4(),
            resourceType: 'Procedure',
            code: {
                coding: [
                    {
                        display: 'Non-billable',
                        version: 'non-billable',
                    },
                ],
            },
            status: 'in-progress',
            subject: data.patient,
            encounter: {
                id: encounter?.id,
                resourceType: 'Encounter',
            },
            perUnitDuration: 0,
        });
        transaction.push(crud.post(resource));
    }

    // Handle regular services
    const regularServices = data.service.filter((ser) => ser.id !== 'non-billable');

    if (regularServices.length > 0) {
        const addOnServicesIds = regularServices
            .filter((ser) => ser.addOn?.length > 0)
            .flatMap((ser) => ser.addOn?.filter((a) => a.includeByDefault).map((a) => a.addOnCode?.id))
            .filter(Boolean);

        const [hcsResult, addOnServicesResult] = await Promise.all([
            $httpFhirApi.get(
                'Encounter?id=' +
                    data.encounter.partOf?.id +
                    '&_include:iterate=Encounter:part-of:Encounter&_include=Encounter:healthcareService&_elements=Encounter.id,HealthcareService.defaultModifier',
            ),
            addOnServicesIds.length > 0 ? $httpFhirApi.get('HealthcareService?id=' + addOnServicesIds.join(',')) : Promise.resolve({ data: [] }),
        ]);

        const addOnServices = addOnServicesResult.data;
        for (const service of regularServices) {
            const procedureId = uuidv4();
            const subject = data.patient;

            // Find the corresponding service from the data passed in
            let resource = new Procedure({
                id: procedureId,
                status: 'in-progress',
                encounter: encounter,
                subject: subject,
                code: { text: service.description, coding: [{ code: service.billingCode, display: service.officeCode || service.name, version: service.id }] },
                unit: service.unit || 1,
                // Only use selected modifiers from the selected service
                modifier: service?.modifier?.filter((m) => m?.includeByDefault) || [],
            });

            // Determine if there is a ProgramDefaultModifier
            let healthcareService = hcsResult.data.find((r) => r.resourceType === 'HealthcareService');

            if (healthcareService?.defaultModifier) {
                if (!resource.modifier.some((existingModifier) => existingModifier.code === healthcareService.defaultModifier && existingModifier.position === 3)) {
                    resource.modifier.push({ code: healthcareService.defaultModifier, position: 3 });
                }
            }

            // Auto-select user's practitioner modifier when setting a new service
            const practitionerModifier = useAuthStore().practitioner.practitionerModifier;
            if (practitionerModifier && !resource.modifier.some((m) => m.code === practitionerModifier.code && m.position === 1)) {
                resource.modifier.push(new ProcedureModifier({ code: practitionerModifier.code, position: 1 }));
            }

            transaction.push(crud.post(resource));

            // sub-procedures (addOn services)
            service.addOn?.forEach((addOn) => {
                const addOnService = addOnServices.find((s) => s.id === addOn.addOnCode?.id);
                if (!addOnService) return;
                let subResource = new Procedure({
                    id: uuidv4(),
                    status: 'in-progress',
                    encounter: encounter,
                    subject: subject,
                    partOf: [{ id: procedureId, resourceType: 'Procedure' }],
                    code: { text: addOnService.name, coding: [{ code: addOnService.billingCode, display: addOnService.name, version: addOnService.id }] },
                    unit: addOn.unit || 1,
                    modifier: addOn.modifier || [],
                });
                transaction.push(crud.post(subResource));
            });
        }

        if (existingTransaction === false) {
            return await postTransaction(transaction);
        }
    }
    return transaction;
};

async function blankQuestionnaire(data, existingTransaction = false) {
    let transaction = existingTransaction || [];

    const authStore = useAuthStore();

    let newForm = new Questionnaire({
        id: uuidv4(),
        resourceType: 'Questionnaire',
        title: data.type === 'live' ? 'Live Form' : 'New Form',
        status: 'draft',
        version: '1',
        subjectType: ['Patient'],
        formType: data.type,
        bnOwner: { id: authStore.organizationAccount.id, resourceType: 'Organization' },
    });

    newForm.url = import.meta.env.VITE_PROTOCOL + authStore.account.fhirApiUri + '/Questionnaire/' + newForm.id;
    transaction.push(crud.post(newForm));
    // add to collection if specified
    if (data.collection) {
        const item = {
            code: 'Form',
            name: 'Form',
            value: {
                uri: newForm.url,
            },
        };
        data.collection.item.push(item);

        transaction.push(crud.patch(data.collection));
    }

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
}

// Publish a draft, or reactivate an inactive form, through the server (#6228): it makes this the family's
// only active version and locks every other active or inactive version with the same url, sparing
// drafts, in one transaction. Only the id is read, so an item-less list row will do. Resolves to
// { resource, locked }; throws on a refusal.
async function publishQuestionnaire(data) {
    const { publishQuestionnaire: publish } = useQuestionnaireApi();
    return await publish(data.questionnaire.id);
}

async function copyQuestionnaire(data, existingTransaction = false) {
    let transaction = existingTransaction || [];

    const authStore = useAuthStore();

    // fetch the questionnaire if a url is provided
    if (data.url) {
        const [profileServer] = data.url.split('/Questionnaire/');
        let managedForm = await $http.get(profileServer + '/Questionnaire?_sort=-version&_count=1&status=active&url=' + data.url);
        managedForm = managedForm.data?.entry?.[0]?.resource;
        if (managedForm?.id) {
            data.questionnaire = new Questionnaire(managedForm);
        }
    }

    // Degrade gracefully when there is no source form to copy (e.g. the profile
    // server returned an empty Bundle) instead of dereferencing an undefined form.
    if (!data.questionnaire) {
        console.warn('copyQuestionnaire: no source questionnaire found for', data.url);
        return transaction;
    }

    let clone = data.questionnaire.toJSON();
    clone.id = uuidv4();
    clone.status = 'draft';
    clone = new Questionnaire(clone);

    // make standalone
    if (data.decouple) {
        clone.derivedFrom = [];
        clone.publisher = undefined;
        unlockFormItems(clone.item);
    }

    // set the bnOwner based on results of confirmation (defaults to brand)
    if (data.owner === 'account') {
        clone.bnOwner = new Reference({ id: authStore.account.id, resourceType: 'Organization' });
    } else {
        clone.bnOwner = new Reference({ id: authStore.brand.id, resourceType: 'Organization' });
    }
    // set additional properties
    clone.title = clone.title + ' - COPY';
    clone.url = import.meta.env.VITE_PROTOCOL + authStore.account.fhirApiUri + '/Questionnaire/' + clone.id;
    clone.version = '1';

    // save the cloned form
    transaction.push(crud.post(clone));

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
}

// Published forms take a propagated interop code or compliance flag through the server, which checks the
// change against the stored form (#6228); drafts keep their JSON patch. Throws on a refusal.
async function savePublishedForms(forms) {
    const { saveQuestionnaire } = useQuestionnaireApi();
    await Promise.all(forms.filter((form) => form.needsSaved?.() !== false).map((form) => saveQuestionnaire(form)));
}

// Published forms take shared-field values through /revise, which accepts only values equal to the
// linked Field Library entry and versions the form with a retired snapshot in one transaction (#6228).
// The client no longer snapshots or bumps them; each form takes the version the server gave it.
// Throws on a refusal.
async function revisePublishedForms(forms) {
    const { reviseQuestionnaire } = useQuestionnaireApi();
    await Promise.all(
        forms.map(async (form) => {
            const result = await reviseQuestionnaire(form);
            if (result?.resource?.version) form.version = result.resource.version;
        }),
    );
}

async function updateCatalogInformationText(data, existingTransaction = false) {
    let transaction = existingTransaction || [];
    let resources = await $httpFhirApi.get('Questionnaire?&status=active,draft&questionnaireField=' + data.code + '&_count=' + PROPAGATION_FETCH_COUNT);

    let questionnaires = resources.data;
    let uniqueQuestionnaires = uniqBy(questionnaires, 'id').filter((e) => e);

    // Only a form whose value actually changes is written, so only it versions.
    const publishedForms = [];
    for (let form of uniqueQuestionnaires) {
        let fields = form.getCatalogFields(data.code);
        if (!fields.some((item) => item.informationText !== data.informationText)) continue;

        for (let item of fields) {
            item.informationText = data.informationText;
        }
        if (form.status === 'active') publishedForms.push(form);
        else transaction.push(crud.patch(form));
    }

    await revisePublishedForms(publishedForms);

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
}

async function updateCatalogEhrOnly(data, existingTransaction = false) {
    let transaction = existingTransaction || [];
    let resources = await $httpFhirApi.get('Questionnaire?&status=active,draft&questionnaireField=' + data.code + '&_count=' + PROPAGATION_FETCH_COUNT);

    let questionnaires = resources.data;
    let uniqueQuestionnaires = uniqBy(questionnaires, 'id').filter((e) => e);

    // Only a form whose value actually changes is written, so only it versions.
    const publishedForms = [];
    for (let form of uniqueQuestionnaires) {
        let fields = form.getCatalogFields(data.code);
        if (!fields.some((item) => item.ehrOnly !== data.ehrOnly)) continue;

        for (let item of fields) {
            item.ehrOnly = data.ehrOnly;
        }
        if (form.status === 'active') publishedForms.push(form);
        else transaction.push(crud.patch(form));
    }

    await revisePublishedForms(publishedForms);

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
}

async function updateCatalogPortalReadOnly(data, existingTransaction = false) {
    let transaction = existingTransaction || [];
    let resources = await $httpFhirApi.get('Questionnaire?&status=active,draft&questionnaireField=' + data.code + '&_count=' + PROPAGATION_FETCH_COUNT);

    let questionnaires = resources.data;
    let uniqueQuestionnaires = uniqBy(questionnaires, 'id').filter((e) => e);

    // Only a form whose value actually changes is written, so only it versions.
    const publishedForms = [];
    for (let form of uniqueQuestionnaires) {
        let fields = form.getCatalogFields(data.code);
        if (!fields.some((item) => item.portalReadOnly !== data.portalReadOnly)) continue;

        for (let item of fields) {
            item.portalReadOnly = data.portalReadOnly;
        }
        if (form.status === 'active') publishedForms.push(form);
        else transaction.push(crud.patch(form));
    }

    await revisePublishedForms(publishedForms);

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
}

async function updateCatalogDeprecate(data, existingTransaction = false) {
    let transaction = existingTransaction || [];
    let resources = await $httpFhirApi.get('Questionnaire?&status=active,draft&questionnaireField=' + data.code + '&_count=' + PROPAGATION_FETCH_COUNT);

    let questionnaires = resources.data;
    let uniqueQuestionnaires = uniqBy(questionnaires, 'id').filter((e) => e);

    // Only a form whose value actually changes is written, so only it versions.
    const publishedForms = [];
    for (let form of uniqueQuestionnaires) {
        let fields = form.getCatalogFields(data.code);
        if (!fields.some((item) => item.deprecate !== data.deprecate)) continue;

        for (let item of fields) {
            item.deprecate = data.deprecate;
        }
        if (form.status === 'active') publishedForms.push(form);
        else transaction.push(crud.patch(form));
    }

    await revisePublishedForms(publishedForms);

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
}

async function updateCatalogRequirementType(data, existingTransaction = false) {
    let transaction = existingTransaction || [];
    let resources = await $httpFhirApi.get('Questionnaire?&status=active,draft&questionnaireField=' + data.code + '&_count=' + PROPAGATION_FETCH_COUNT);

    let questionnaires = resources.data;
    let uniqueQuestionnaires = uniqBy(questionnaires, 'id').filter((e) => e);

    const publishedForms = [];
    for (let form of uniqueQuestionnaires) {
        let fields = form.getCatalogFields(data.code);

        for (let item of fields) {
            item.isCarf = data.isCarf;
            item.isJointCommission = data.isJointCommission;
            item.isCms = data.isCms;
        }
        if (form.status === 'active') publishedForms.push(form);
        else transaction.push(crud.patch(form));
    }

    await savePublishedForms(publishedForms);

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
}

async function updateCatalogText(data, existingTransaction = false) {
    let transaction = existingTransaction || [];
    let resources = await $httpFhirApi.get('Questionnaire?&status=active,draft&questionnaireField=' + data.code + '&_count=' + PROPAGATION_FETCH_COUNT);

    let questionnaires = resources.data;
    let uniqueQuestionnaires = uniqBy(questionnaires, 'id').filter((e) => e);

    const publishedForms = [];
    for (let form of uniqueQuestionnaires) {
        let fields = form.getCatalogFields(data.code);

        for (let item of fields) {
            item.bnText = data.text;
        }
        if (form.status === 'active') publishedForms.push(form);
        else transaction.push(crud.patch(form));
    }

    await revisePublishedForms(publishedForms);

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
}

async function updateCatalogType(data, existingTransaction = false) {
    let transaction = existingTransaction || [];
    let resources = await $httpFhirApi.get('Questionnaire?&status=active,draft&questionnaireField=' + data.code + '&_count=' + PROPAGATION_FETCH_COUNT);

    let questionnaires = resources.data;
    let uniqueQuestionnaires = uniqBy(questionnaires, 'id').filter((e) => e);

    const publishedForms = [];
    for (let form of uniqueQuestionnaires) {
        let fields = form.getCatalogFields(data.code);

        for (let item of fields) {
            // getCatalogFields matches by code, which may include display items that
            // must not be demoted — skip type assignment to preserve their display type.
            if (item.type === 'display') continue;
            item.type = data.type;
        }
        if (form.status === 'active') publishedForms.push(form);
        else transaction.push(crud.patch(form));
    }

    await revisePublishedForms(publishedForms);

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
}

async function updateCatalogCode(data, existingTransaction = false) {
    let transaction = existingTransaction || [];
    let resources = await $httpFhirApi.get('Questionnaire?status=active,draft&questionnaireField=' + data.id + '&_count=' + PROPAGATION_FETCH_COUNT);
    let codes = data.code.filter((e) => e.code)?.length ? data.code.filter((e) => e.code) : [];
    let questionnaires = resources.data;
    let uniqueQuestionnaires = uniqBy(questionnaires, 'id').filter((e) => e);

    const publishedForms = [];
    uniqueQuestionnaires.forEach((form) => {
        let fields = form.getCatalogFields(data.id);
        fields.forEach((item) => {
            let questionnaireFieldCode = item.code.find((e) => e.system === 'http://fhir.bestnotes.com/BN_QuestionnaireField');
            item.code = codes?.length ? [...codes, questionnaireFieldCode] : [questionnaireFieldCode];
        });
        if (form.status === 'active') publishedForms.push(form);
        else transaction.push(crud.patch(form));
    });

    await savePublishedForms(publishedForms);

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
}

async function updateChoicesFromCatalog(data, existingTransaction = false) {
    let transaction = existingTransaction || [];
    let formsRequest = await $httpFhirApi.get('Questionnaire?status=active,draft&questionnaireField=' + data.catalog.id + '&_count=' + PROPAGATION_FETCH_COUNT);
    let uniqueQuestionnaires = uniqBy(formsRequest.data, 'id').filter((e) => e);

    const publishedForms = [];
    for (let questionnaire of uniqueQuestionnaires) {
        if (questionnaire.id !== data.formId) {
            let items = questionnaire.getCatalogFields(data.catalog.id);

            for (let item of items) {
                item.answerOption = data.catalog.choice?.map((e) => new QuestionnaireAnswerOption({ score: e.score, value: { Coding: { display: e.display, code: e.code } } }));
            }
        }
        // The open form is versioned too, as before; its own choices arrive with its save.
        if (questionnaire.status === 'active') publishedForms.push(questionnaire);
        else transaction.push(crud.patch(questionnaire));
    }

    await revisePublishedForms(publishedForms);

    if (existingTransaction === false) {
        await postTransaction(transaction);
        return uniqueQuestionnaires;
    }

    return transaction;
}

async function getObservations(items, subject, existingTransaction = false) {
    const durationTypeMap = {
        a: 'years',
        mo: 'months',
        wk: 'weeks',
        d: 'days',
        h: 'hours',
        "'": 'minutes',
        "''": 'seconds',
    };

    // Filter to only items with observationLinkPeriod and code upfront
    const validItems = items.filter((item) => item.observationLinkPeriod && item.code);

    // Early return if no valid items
    if (validItems.length === 0) {
        if (existingTransaction === false) {
            return {};
        }
        return existingTransaction || [];
    }

    const now = new Date();

    // Separate items that need _include=has-member (must remain individual FHIR queries)
    const questionItems = validItems.filter((item) => item.type === 'question');
    const batchableItems = validItems.filter((item) => item.type !== 'question');

    // Group batchable items by date constraint to minimize AidboxQuery calls
    // Key: minDate string, Value: { items: [], codes: Set }
    const dateGroups = new Map();

    for (const item of batchableItems) {
        const durationKey = durationTypeMap[item.observationLinkPeriod.code];
        const date = sub(now, { [durationKey]: parseInt(item.observationLinkPeriod.value) });
        const minDate = format(date, 'yyyy-MM-dd');

        if (!dateGroups.has(minDate)) {
            dateGroups.set(minDate, { items: [], codes: new Set(), linkIdToCodeKeys: new Map() });
        }

        const group = dateGroups.get(minDate);
        group.items.push(item);

        // Track which codes belong to which linkId
        const codeKeys = [];
        for (const coding of item.code) {
            if (coding.system !== 'http://fhir.bestnotes.com/BN_QuestionnaireField') continue;
            group.codes.add(coding.code);
            codeKeys.push(coding.code);
        }
        group.linkIdToCodeKeys.set(item.linkId, codeKeys);
    }

    // Build results
    const returnResults = {};

    // Process batched items using AidboxQuery (parallel calls for each date group)
    const dateGroupEntries = Array.from(dateGroups.entries());

    const batchPromises = dateGroupEntries.map(([minDate, group]) => {
        const codesString = Array.from(group.codes).join(',');
        return $httpFhirApi
            .get(`/$query/most-recent-observations-per-code`, {
                params: {
                    subject_id: subject.id,
                    codes: codesString,
                    min_date: minDate,
                },
            })
            .then((response) => ({ success: true, response, group }))
            .catch((error) => ({ success: false, error, group }));
    });

    const batchResults = await Promise.all(batchPromises);

    // Process batch results
    for (const result of batchResults) {
        const { group } = result;

        if (result.success) {
            // Build a map of code -> array of observation values
            // Query returns: id, value, code, effective_date, performer, derived_from
            // Multiple rows can exist per code (e.g., multi-select choice items)
            const codeToValues = new Map();
            for (const row of result.response.originalData.data || []) {
                if (row.value) {
                    const entry = { value: row.value, performer: row.performer, derivedFrom: row.derived_from, instant: row.effective_date };
                    if (codeToValues.has(row.code)) {
                        codeToValues.get(row.code).push(entry);
                    } else {
                        codeToValues.set(row.code, [entry]);
                    }
                }
            }

            // Map results back to each item's linkId
            for (const item of group.items) {
                const itemCodeKeys = group.linkIdToCodeKeys.get(item.linkId);

                if (item.type === 'choice') {
                    // For choice items, return all matching values for any of the item's codes
                    const matchingValues = [];
                    for (const codeKey of itemCodeKeys) {
                        if (codeToValues.has(codeKey)) {
                            matchingValues.push(...codeToValues.get(codeKey));
                        }
                    }
                    returnResults[item.linkId] = matchingValues;
                } else {
                    // For other items, return the first value for any of the item's codes
                    let found = false;
                    for (const codeKey of itemCodeKeys) {
                        if (codeToValues.has(codeKey)) {
                            returnResults[item.linkId] = codeToValues.get(codeKey);
                            found = true;
                            break;
                        }
                    }
                    if (!found) {
                        returnResults[item.linkId] = [];
                    }
                }
            }
        } else {
            console.error('Error fetching observations via AidboxQuery:', result.error);
            // On error, set empty results for all items in this group
            for (const item of group.items) {
                returnResults[item.linkId] = [];
            }
        }
    }

    // Process question items individually (need _include=has-member)
    if (questionItems.length > 0) {
        let transaction = existingTransaction || [];
        const questionIndexMap = {};
        let index = transaction.length;
        const baseParams = `subject=${subject.id}&status=final,amended,corrected&_sort=-date`;

        for (const item of questionItems) {
            const codesForQueryString = item.code.map((coding) => `${coding.system}|${coding.code}`).join(',');
            const durationKey = durationTypeMap[item.observationLinkPeriod.code];
            const date = sub(now, { [durationKey]: parseInt(item.observationLinkPeriod.value) });
            const dateString = 'ge' + format(date, 'yyyy-MM-dd');

            // Each field set row is its own panel, so one panel is one row. Take a page of them and
            // keep only the most recent form's below.
            const queryString = `/Observation?${baseParams}&code=${codesForQueryString}&date=${dateString}&_count=${FIELD_SET_ROW_FETCH_COUNT}&_include=has-member`;

            transaction.push({
                request: {
                    method: 'GET',
                    url: queryString,
                },
            });
            questionIndexMap[item.linkId] = index++;
        }

        if (existingTransaction === false) {
            const results = await postTransaction(transaction);
            const unnumbered = [];

            for (const linkId in questionIndexMap) {
                const idx = questionIndexMap[linkId];
                const entries = results[idx]?.entry || [];

                if (entries.length <= 1) {
                    returnResults[linkId] = entries;
                } else {
                    // Keep the newest form's Observations, and of those only its latest lock's: a form
                    // locked more than once can still hold an earlier lock's beside the current ones,
                    // and every Observation one lock writes shares its effective instant. The newest
                    // panel is found by that instant rather than by where the server put it.
                    const newest = newestPanel(entries) || entries[0];
                    const derivedFromIds = newest?.derivedFrom?.map((from) => from?.id) || [];
                    const latestRun = newest?.effective?.instant;
                    const fromNewestForm = entries.filter(
                        (ob) => ob.derivedFrom?.some((dr) => derivedFromIds.includes(dr.id)) && (!latestRun || !ob.effective?.instant || ob.effective.instant === latestRun),
                    );
                    // Put the rows back in the order they were entered.
                    const panels = fromNewestForm.filter((ob) => ob.hasMember?.length).sort(byFieldSetRow);
                    const members = fromNewestForm.filter((ob) => !ob.hasMember?.length);
                    returnResults[linkId] = [...panels, ...members];
                    if (panels.length > 1 && panels.every((panel) => fieldSetRowIndex(panel) === Infinity) && derivedFromIds[0]) {
                        unnumbered.push({ linkId, panels, members, responseId: derivedFromIds[0] });
                    }
                }
            }

            await orderUnnumberedRows(unnumbered, returnResults);
        } else {
            // If using existing transaction, return the transaction for later processing
            return transaction;
        }
    }

    return returnResults;
}

/**
 * Fill a field set's rows from the Observations latest value found for it: one row per panel,
 * each member placed in its row by its BN_QuestionnaireField code.
 *
 * The rows are rebuilt rather than added to. A live form fills itself every time it opens, so it
 * already holds the rows (and answers) it was filled with last time, and keeping those beside
 * the new ones repeated every row after the first and every answer in the first.
 */
function applyFieldSetObservations(questionnaireResponse, questionnaireItem, linkId, observations) {
    const found = (observations || []).filter((observation) => observation.hasMember?.length > 0);
    // A field set that takes one answer gets one row, whatever the form it came from allowed.
    const panels = questionnaireItem.repeats ? found : found.slice(0, 1);
    const firstRow = questionnaireResponse.item.find((responseItem) => responseItem.linkId === linkId);
    if (!panels.length || !firstRow) return;

    const template = firstRow.getObjectCopy();
    template.item?.forEach((child) => {
        child.answer = [];
    });

    const rows = panels.map((panel) => {
        const row = template.getObjectCopy();
        row.linkId = linkId;
        const memberIds = panel.hasMember.map((reference) => reference.id);

        for (const member of observations.filter((observation) => memberIds.includes(observation.id))) {
            const code = member.code?.coding?.find((coding) => coding.system === 'http://fhir.bestnotes.com/BN_QuestionnaireField')?.code;
            // observations written under a catalog code the form no longer carries have nothing to populate
            if (!code) continue;

            const childLinkId = questionnaireItem.item.find((child) => child.getQuestionnaireFieldCode() === code)?.linkId;
            // response items are only built for answerable children, so a display item has no match
            const child = childLinkId && row.item.find((responseChild) => responseChild.linkId === childLinkId);
            if (!child) continue;

            child.auditSource = member.derivedFrom?.[0];
            child.auditUser = member.performer?.[0];
            child.auditLastUpdated = member.effective?.instant;
            child.answer.push(new QuestionnaireResponseItemAnswer({ value: member.value }));
        }
        return row;
    });

    // The first row keeps its place (and object) in the response; the rest replace any rows after it.
    firstRow.item = rows[0].item;
    for (let index = questionnaireResponse.item.length - 1; index >= 0; index--) {
        const responseItem = questionnaireResponse.item[index];
        if (responseItem.linkId === linkId && responseItem !== firstRow) questionnaireResponse.item.splice(index, 1);
    }
    questionnaireResponse.item.push(...rows.slice(1));
}

async function syncFieldSetAcrossForms(data, existingTransaction = false) {
    // Validate that this is actually a field set with items
    if (!data.fieldSet || !data.fieldSet.item || !data.fieldSet.item.length) {
        return existingTransaction || [];
    }

    let transaction = existingTransaction || [];

    // Query for all questionnaires that contain this field set
    let formsRequest = await $httpFhirApi.get('Questionnaire?status=active,draft&questionnaireField=' + data.fieldSet.id + '&_count=' + PROPAGATION_FETCH_COUNT);
    let uniqueQuestionnaires = uniqBy(formsRequest.data, 'id').filter((e) => e);

    // Desired member state, serialized once so every form is compared against the same
    // snapshot and the write below stores an identical shape (unchanged forms round-trip equal).
    const desiredJson = JSON.stringify(data.fieldSet.item || []);
    const desiredCanonical = canonicalizeItemIdentity(data.fieldSet.item);

    const publishedForms = [];
    for (let questionnaire of uniqueQuestionnaires) {
        // Skip the current form being edited
        if (questionnaire.id === data.formId) {
            continue;
        }

        // Find all instances of this field set in the questionnaire
        let fieldSets = questionnaire.getCatalogFields(data.fieldSet.id);

        // Only proceed if we found field sets to update in this questionnaire
        if (!fieldSets || fieldSets.length === 0) {
            continue;
        }

        // Skip forms whose members already match — a form versions when its content changes,
        // not on a no-op re-save (form-builder.md: "versions when its meaning changes"). Without
        // this guard, re-saving an unchanged field set would snapshot + minor-bump every active
        // form using it. Mirrors the needsUpdate guard in the updateCatalog* siblings.
        //
        // Compared identity-insensitively: member linkIds are per-form identity, not shared content
        // (que-2 forces them to diverge when one form holds the set twice), and adding a set to a
        // form re-mints them. A re-identified but otherwise identical set is not a change of meaning.
        const needsUpdate = fieldSets.some((fieldSet) => canonicalizeItemIdentity(fieldSet.item) !== desiredCanonical);
        if (!needsUpdate) {
            continue;
        }

        // Update all instances of the field set
        for (let fieldSet of fieldSets) {
            // Deep-copy the source children, then give this instance its own fresh child linkIds
            // and remap intra-set enableWhen/scoreExpression references onto them. Copying the
            // source linkIds verbatim would collide when a form holds the same set more than once
            // (FHIR que-2) and leave rules pointing at linkIds that only exist in the source form.
            // Wrapping in a container-less node keeps this instance's own container linkId untouched.
            const copiedItems = JSON.parse(desiredJson);
            regenerateItemLinkIds({ item: copiedItems });
            fieldSet.item = copiedItems;
        }

        // Published forms version through /revise, drafts take the patch
        if (questionnaire.status === 'active') publishedForms.push(questionnaire);
        else transaction.push(crud.patch(questionnaire));
    }

    await revisePublishedForms(publishedForms);

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
}
const getIndividualEncounterIds = async (encounterId, allEncounters = []) => {
    const allIds = new Set([encounterId]);

    if (allEncounters.length > 0) {
        const currentEncounter = allEncounters.find((e) => e.id === encounterId);
        if (currentEncounter) {
            const childEncounters = allEncounters.filter((e) => e.resourceType === 'Encounter' && e.partOf?.id === encounterId);
            childEncounters.forEach((child) => {
                if (child.id) {
                    allIds.add(child.id);
                }
            });

            if (currentEncounter.partOf?.id) {
                const parentId = currentEncounter.partOf.id;
                const siblings = allEncounters.filter((e) => e.resourceType === 'Encounter' && e.partOf?.id === parentId && e.id !== encounterId);
                siblings.forEach((sibling) => {
                    if (sibling.id) {
                        allIds.add(sibling.id);
                    }
                });
            }
        }
    } else {
        const response = await $httpFhirApi.get(`Encounter?_id=${encounterId}&_include=Encounter:part-of&_revinclude:iterate=Encounter:part-of&_elements=Encounter.id`);
        const encounters = response.data || [];
        encounters.forEach((e) => {
            if (e.id) allIds.add(e.id);
        });
    }

    return Array.from(allIds);
};

const getGroupEncounterIds = async (encounterId, allEncounters = []) => {
    const allIds = new Set([]);

    if (allEncounters.length > 0) {
        const currentEncounter = allEncounters.find((e) => e.id === encounterId);
        if (currentEncounter) {
            allIds.add(encounterId);
            const childEncounters = allEncounters.filter((e) => e.resourceType === 'Encounter' && e.partOf?.id === encounterId);
            childEncounters.forEach((child) => {
                if (child.id) {
                    allIds.add(child.id);
                }
            });
        }
    } else {
        const response = await $httpFhirApi.get(`Encounter?_id=${encounterId}&_include:iterate=Encounter:part-of:Encounter&_revinclude=Encounter:part-of&_elements=Encounter.id`);
        const encounterIds = response.data.map((e) => e.id);
        allIds.add(...encounterIds);
    }

    return Array.from(allIds);
};

const checkFormsHaveSubmittedClaims = async (formIds, encounterId = null, allEncounters = [], isGroupEncounter = false) => {
    const actualFormIds = Array.isArray(formIds) ? formIds : [formIds];

    if (!actualFormIds.length || !encounterId) {
        return [];
    }

    // Use appropriate function based on encounter type
    const allEncounterIds = isGroupEncounter ? await getGroupEncounterIds(encounterId, allEncounters) : await getIndividualEncounterIds(encounterId, allEncounters);

    if (!allEncounterIds.length) {
        return [];
    }

    // Fetch claims for all encounters in a single request
    const claims = (await $httpFhirApi.get(`Claim?encounter=${allEncounterIds.join(',')}&_elements=status,supportingInfo`)).data;

    // Extract all QuestionnaireResponse references from active claims' supportingInfo
    const referencedFormIds = new Set(
        claims
            .filter((claim) => claim.status === 'active')
            .flatMap((claim) => claim.supportingInfo || [])
            .map((info) => info.code?.text?.match(/QuestionnaireResponse\/([^/\s]+)/)?.[1])
            .filter(Boolean),
    );

    // Return only the form IDs that were passed in and have claims
    return actualFormIds.filter((formId) => referencedFormIds.has(formId));
};

async function setManagedFormInactive(data) {
    let transaction = [];
    const authStore = useAuthStore();

    // Normalize to the base catalog url (strip any |version suffix) so the derivedFrom
    // comparison matches how importForm stores it.
    const catalogBaseUrl = getUrlFromCanonical(data.catalogUrl).url;

    // Only the LOCAL tenant server is queried/written — the catalog/profile record is never touched.
    // The marker keeps this off every other derived copy of the same catalog form: the tenant's working
    // copies and their retired version-history snapshots share derivedFrom, and must never be flipped here.
    const existing = await $httpFhirApi.get(`/Questionnaire?${MANAGED_FORM_SUPPRESSION_QUERY}&_count=1&derivedFrom=${catalogBaseUrl}`);
    let derivedCopy = existing.data?.[0];

    if (derivedCopy) {
        // An existing marker is no longer a draft, so its status flips through the server (#6228).
        derivedCopy.status = data.inactive ? 'retired' : 'active';
        const { saveQuestionnaire } = useQuestionnaireApi();
        await saveQuestionnaire(derivedCopy);
        return derivedCopy;
    } else {
        // Nothing to reactivate — the form is already available, so don't mint a marker for it.
        if (!data.inactive) return null;

        // First inactivation — mint a thin marker copy (mechanically like useProfileFlow.importForm,
        // but born retired, marked, and with no items).
        derivedCopy = new Questionnaire({ id: 'mf-' + uuidv4(), title: data.catalogTitle, formType: data.formType });
        derivedCopy.derivedFrom = [catalogBaseUrl];
        derivedCopy.identifier = [{ system: MANAGED_FORM_SUPPRESSION_SYSTEM, value: MANAGED_FORM_SUPPRESSION_VALUE }];
        derivedCopy.url = import.meta.env.VITE_PROTOCOL + authStore.account.fhirApiUri + '/Questionnaire/' + derivedCopy.id;
        derivedCopy.status = 'retired';
        transaction.push(crud.post(derivedCopy));
    }

    await postTransaction(transaction);
    return derivedCopy;
}

// Fetch a form with its questions. List queries strip `item` (_elements=-item), so any action that
// clones or renders a form has to fetch the full resource by id first.
async function fetchQuestionnaireWithItems(id) {
    if (!id) return null;
    try {
        const response = await $httpFhirApi.get('Questionnaire/' + id);
        const resource = response?.data;
        if (!resource?.id) return null;
        return resource instanceof Questionnaire ? resource : new Questionnaire(resource);
    } catch (error) {
        console.error('[useQuestionnaireFlow] Failed to fetch questionnaire', id, error);
        return null;
    }
}

// Status and deprecation changes go through the server, which checks them against the stored form
// (#6228). A list row is item-less, and the server takes the whole form, so the change is made on the
// stored form fetched by id — saving the row itself would delete every question. The row takes the
// change too. Resolves to the saved resource, or null when the form could not be read; throws on a
// refusal.
async function saveFormChange(questionnaire, change) {
    const form = await fetchQuestionnaireWithItems(questionnaire?.id);
    if (!form) return null;

    change(form);
    const { saveQuestionnaire } = useQuestionnaireApi();
    const saved = await saveQuestionnaire(form);
    change(questionnaire);
    return saved;
}

async function inactivateQuestionnaire(data) {
    return saveFormChange(data.questionnaire, (form) => {
        form.status = 'retired';
    });
}

async function deprecateQuestionnaire(data) {
    return saveFormChange(data.questionnaire, (form) => {
        form.deprecate = true;
    });
}

// The other half of deprecateQuestionnaire. Deprecation is independent of status, so undoing it
// clears the flag and nothing else — a deprecated form is usually still published.
async function undeprecateQuestionnaire(data) {
    return saveFormChange(data.questionnaire, (form) => {
        form.deprecate = false;
    });
}

async function unpublishQuestionnaire(data) {
    return saveFormChange(data.questionnaire, (form) => {
        form.status = 'draft';
    });
}

// Clone a form as a new draft with the major version bumped. A form family may only have one draft at
// a time, so an existing draft aborts the operation.
async function versionQuestionnaire(data, existingTransaction = false) {
    let transaction = existingTransaction || [];

    const questionnaire = await fetchQuestionnaireWithItems(data.id);
    if (!questionnaire) return null;

    const existingDraft = await $httpFhirApi.get(`Questionnaire?url=${questionnaire.url}&status=draft&_elements=id`);
    if (existingDraft?.data?.some((form) => form.id !== questionnaire.id)) return null;

    let clone = questionnaire.toJSON();
    clone.id = uuidv4();
    clone.status = 'draft';
    clone = new Questionnaire(clone);
    clone.version = (parseInt(clone.version) + 1).toString();

    transaction.push(crud.post(clone));

    if (existingTransaction === false) {
        const result = await postTransaction(transaction);
        return result ? { id: clone.id } : null;
    }

    return transaction;
}

async function deleteQuestionnaireDraft({ questionnaire, collectionType = 'formCollection' }) {
    if (!questionnaire?.id) return false;

    // The list removal has to complete before the delete so the refreshed collection data is accurate.
    const { removeItemFromAllLists } = useListFlow();
    await removeItemFromAllLists({ type: collectionType, id: questionnaire.id });

    try {
        await $httpFhirApi.delete('/Questionnaire/' + questionnaire.id);
        return true;
    } catch (error) {
        console.error('[useQuestionnaireFlow] Failed to delete questionnaire draft', questionnaire.id, error);
        return false;
    }
}

async function findPublishedVersionId(url) {
    if (!url) return null;
    try {
        const response = await $httpFhirApi.get(`Questionnaire?url=${url}&status=active&_elements=id`);
        return response?.data?.[0]?.id || null;
    } catch (error) {
        console.warn('Failed to find published version by URL:', error);
        return null;
    }
}

export function useQuestionnaireFlow() {
    return {
        lock,
        unlock,
        updateSignerReviewer,
        signReview,
        initializeQuestionnaire,
        blankQuestionnaire,
        publishQuestionnaire,
        copyQuestionnaire,
        updateCatalogInformationText,
        updateCatalogText,
        updateCatalogType,
        updateCatalogCode,
        updateCatalogEhrOnly,
        updateCatalogPortalReadOnly,
        updateCatalogRequirementType,
        findQuestionnaireItemByLinkId,
        filterQuestionnaireItemByLinkId,
        findQuestionnaireItem,
        filterQuestionnaireItems,
        collectNavigationItems,
        toNavEntry,
        extractQuestionnaireResponse,
        getObservations,
        removeObservations,
        applyFieldSetObservations,
        updateChoicesFromCatalog,
        updateCatalogDeprecate,
        unlockSignatureReview,
        syncFieldSetAcrossForms,
        getIndividualEncounterIds,
        getGroupEncounterIds,
        cleanupChargeItemsForQuestionnaireResponse,
        checkFormsHaveSubmittedClaims,
        addProcedure,
        completeProceduresForEncounter,
        setManagedFormInactive,
        fetchQuestionnaireWithItems,
        inactivateQuestionnaire,
        deprecateQuestionnaire,
        undeprecateQuestionnaire,
        unpublishQuestionnaire,
        versionQuestionnaire,
        deleteQuestionnaireDraft,
        findPublishedVersionId,
    };
}
